import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC } from 'react';
import { Account } from './Account/Account';
import {
  AuthInfoState,
  LoginInfoState,
  SelectedTenant,
} from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { ThemeId } from '@/consts/shop';
import { useFetchTenantSwitchSettings } from '@/hooks/api/tenant/useFetchTenantSwitchSettings';
import {
  Car,
  Customer,
  NextWarranty,
  PaymentMethod,
  Prepaid,
  Warranty,
} from '@/types/api/customerApi';
import { SwitchTargetTenant } from '@/types/api/tenantApi';
import { selectedTenantFor } from '@/utils/selectedTenant';
import { storeDispatch } from '@/utils/store';

interface AccountPageProps {
  f7router: Router.Router;
  status: string;
}
export const AccountPage: FC<AccountPageProps> = ({ f7router, status }) => {
  const themeId = useStore(store, 'getThemeId') as ThemeId;
  const customerInfo = useStore(store, 'customerInfo') as Required<Customer>;
  const authInfo = useStore(store, 'authInfo') as AuthInfoState;
  const paymentMethodsInfo = useStore(
    store,
    'getPaymentMethodsInfo',
  ) as Required<PaymentMethod>[];
  const prepaidInfo = useStore(
    store,
    'getAdvancePaymentsInfo',
  ) as Required<Prepaid>[];
  const loginInfo = useStore(store, 'loginInfo') as LoginInfoState;
  const currentWarranty = useStore(store, 'getCurrentWarranty') as
    | Warranty
    | undefined;
  const nextWarranty = useStore(store, 'getNextWarranty') as
    | NextWarranty
    | undefined;
  const carItemInfo = useStore(store, 'carItemInfo') as Required<Car>;
  const carsInfo = useStore(store, 'carsInfo') as Required<Car>[];
  const selectedTenant = useStore(store, 'getSelectedTenant') as SelectedTenant;

  // テナント切り替え設定を取得
  const isOwnCustomer =
    authInfo.m_customer_id > 0 && customerInfo.id === authInfo.m_customer_id;
  // customerInfo.m_affiliate_id は所属法人。customerInfoAffiliateId は
  // 切り替え先を含む「基本情報の取得先」であり、候補取得元には使わない。
  const ownAffiliateId = isOwnCustomer ? customerInfo.m_affiliate_id : 0;
  const {
    tenantSwitchEnabled,
    switchTargets,
    error: tenantSwitchError,
    retry: retryTenantSwitchSettings,
  } = useFetchTenantSwitchSettings({
    m_affiliate_id: ownAffiliateId,
    enabled: isOwnCustomer,
  });

  // テナント選択ハンドラー
  // ストア更新を待ってから遷移する。待たないとホーム側が切り替え前の法人で
  // 描画を始めてしまい、あとから内容が入れ替わる。
  const handleSelectTenant = async (tenant: SwitchTargetTenant | null) => {
    if (customerInfo.id !== authInfo.m_customer_id) return;
    await storeDispatch(
      'setSelectedTenant',
      'selectedTenant',
      tenant
        ? {
            ...tenant,
            source_m_customer_id: store.state.authInfo.m_customer_id,
          }
        : null,
    );
    // ホームの再描画で遷移中の page 要素が差し替わると、アニメーション完了を
    // 検知できず router がロックされる。切り替え結果はアニメーションなしで表示する。
    f7.view.main.router.navigate(paths.home, { animate: false });
  };

  return (
    <Account
      f7router={f7.views.main.router}
      themeId={themeId}
      status={status}
      customerInfo={customerInfo}
      paymentMethodsInfo={paymentMethodsInfo}
      prepaidInfo={prepaidInfo}
      loginInfo={loginInfo}
      currentWarranty={currentWarranty}
      nextWarranty={nextWarranty}
      carItemInfo={carItemInfo}
      carsInfo={carsInfo}
      tenantSwitchEnabled={tenantSwitchEnabled}
      tenantSwitchError={Boolean(tenantSwitchError)}
      onRetryTenantSettings={retryTenantSwitchSettings}
      switchTargets={switchTargets}
      selectedTenant={
        customerInfo.id === authInfo.m_customer_id
          ? selectedTenantFor(customerInfo.id, selectedTenant)
          : null
      }
      onSelectTenant={handleSelectTenant}
    />
  );
};
